import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import useSeo from "@/lib/useSeo";

export default function TeamInvite() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const nav = useNavigate();

  useSeo({
    title: "Join your board director's cascade — The Value Realisation Index",
    description: "You've been invited to complete the Value Realisation Index as part of your board director's team.",
    path: `/team-invite/${token}`,
    noindex: true,
  });

  useEffect(() => {
    api.get(`/board-members/by-team-token/${token}`)
      .then(({ data }) => setInfo(data))
      .catch(e => setError(e.response?.data?.detail || "Invitation not recognised"));
  }, [token]);

  if (error) return <div className="min-h-screen bg-linen"><Navbar /><div className="max-w-3xl mx-auto p-12 text-heritage" data-testid="invite-error">{error}</div><Footer /></div>;
  if (!info) return <div className="min-h-screen bg-linen"><Navbar /><div className="max-w-3xl mx-auto p-12 tick-label">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />

      <section className="max-w-4xl mx-auto px-6 lg:px-12 pt-24 pb-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="tick-label-accent mb-6">You've been invited</div>
          <h1 className="display-serif text-5xl md:text-7xl leading-tight">
            Your view of reality<br/>
            <em className="italic text-orange">matters</em> here.
          </h1>
          <div className="rule-orange my-10" />
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-8">
          <div className="text-lg text-charcoal leading-relaxed space-y-5">
            <p>
              <span className="text-oxford">{info.board_member_name}</span>{info.board_member_role ? <>, {info.board_member_role}, </> : " "}
              is running the <span className="text-oxford">Value Realisation Index</span> across their board at{" "}
              <span className="text-oxford">{info.organisation}</span> — and has invited you, as a direct report, to answer the same twenty-five questions.
            </p>
            <p>
              Your individual answers are confidential. They contribute to an
              anonymised cascade view showing whether {info.board_member_name.split(" ")[0]}'s team is seeing the same organisation they are.
            </p>
            <p className="italic">
              Twenty minutes. Twenty-five questions. First-instinct answers are the most accurate.
            </p>
          </div>

          <div className="border border-oxford/15 bg-linen p-8">
            <div className="tick-label-accent mb-4">Who you are answering as</div>
            <div className="display-serif text-2xl">A direct report of</div>
            <div className="display-serif text-4xl text-orange mt-2">{info.board_member_name}</div>
            <div className="tick-label mt-2">{info.organisation}</div>

            <button
              data-testid="invite-begin"
              onClick={() => nav(`/diagnostic?team=${token}`)}
              className="btn-orange w-full justify-center mt-10"
            >
              Begin the Index <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5}/>
            </button>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
